import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { apiService } from '../services/api'
import { useAuth } from './AuthContext'

interface WarzoneCoinsContextType {
  saldo: number | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const WarzoneCoinsContext = createContext<WarzoneCoinsContextType | undefined>(undefined)

export const useWarzoneCoins = () => {
  const ctx = useContext(WarzoneCoinsContext)
  if (!ctx) throw new Error('useWarzoneCoins deve ser usado dentro de WarzoneCoinsProvider')
  return ctx
}

const POLL_INTERVAL_MS = 30_000

export const WarzoneCoinsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()

  const roles = useMemo(() => (user?.roles ?? []).map((r) => String(r).toLowerCase()), [user?.roles])
  const isAdmin = roles.some((r) => ['admin', 'administrator'].includes(r))
  const isUser = roles.some((r) => r === 'user' || r === 'role_user')
  const enabled = isAuthenticated && !!user && isUser && !isAdmin

  const [saldo, setSaldo] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inFlightRef = useRef<Promise<void> | null>(null)

  const refresh = useCallback(async () => {
    if (!enabled) return
    if (inFlightRef.current) return inFlightRef.current

    const run = (async () => {
      setError(null)
      setIsLoading((prev) => prev || saldo === null)
      try {
        const extrato = await apiService.getWarzoneCoinsExtrato({ page: 0, size: 1 })
        setSaldo(Number(extrato?.saldo ?? 0) || 0)
      } catch (e: any) {
        setError(e?.message || 'Erro ao consultar extrato de Warzone Coins.')
      } finally {
        setIsLoading(false)
        inFlightRef.current = null
      }
    })()

    inFlightRef.current = run
    return run
  }, [enabled, saldo])

  useEffect(() => {
    if (!enabled) {
      setSaldo(null)
      setError(null)
      setIsLoading(false)
      return
    }

    void refresh()

    const id = window.setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh()
    }

    const onFocus = () => void refresh()

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)

    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, refresh])

  const value = useMemo<WarzoneCoinsContextType>(
    () => ({
      saldo,
      isLoading,
      error,
      refresh
    }),
    [error, isLoading, refresh, saldo]
  )

  return <WarzoneCoinsContext.Provider value={value}>{children}</WarzoneCoinsContext.Provider>
}
